import { TokenType } from "@gain/common-enum";
import { storageUtils } from "#shared/lib/storage-utils.ts";

export interface JwtPayload {
  userId?: string;
  companyId?: string;
  email?: string;
  name?: string | null;
  profileUrl?: string | null;
  impersonatedBy?: string;
  exp?: number;
}

function tokenKey(type: TokenType, contextId: string): string {
  return `gain_jwt_${type}_${contextId}`;
}

export function parseJwtPayload(token: string): JwtPayload | null {
  try {
    return JSON.parse(atob(token.split(".")[1])) as JwtPayload;
  } catch {
    return null;
  }
}

export function parseJwtExp(token: string): number | null {
  return parseJwtPayload(token)?.exp ?? null;
}

export function isTokenExpired(token: string): boolean {
  const exp = parseJwtExp(token);
  if (exp == null) return false;
  return Date.now() >= (exp - 20) * 1000;
}

export function removeToken(type: TokenType, contextId: string): void {
  storageUtils.delete(tokenKey(type, contextId));
}

/** Reads a stored JWT without removing it when expired. */
export function peekToken(type: TokenType, contextId: string): string | null {
  return storageUtils.get(tokenKey(type, contextId));
}

export function isImpersonationToken(token: string): boolean {
  return typeof parseJwtPayload(token)?.impersonatedBy === "string";
}

export function loadToken(type: TokenType, contextId: string): string | null {
  const token = peekToken(type, contextId);
  if (!token) return null;
  if (isTokenExpired(token)) {
    removeToken(type, contextId);
    return null;
  }
  return token;
}

export function persistToken(
  type: TokenType,
  contextId: string,
  token: string,
): void {
  storageUtils.set(tokenKey(type, contextId), token);
}

export function removeTokens(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("gain_jwt_")) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
}
